import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, MessageCircle, Sparkles, Shield, Zap, BookOpen, GraduationCap } from 'lucide-react';
import api from '../services/api';

const Pricing = () => {
  const [price, setPrice] = useState(5000);

  useEffect(() => {
    api.get('/store-config/public').then(res => {
      if (res.data.config?.price) setPrice(res.data.config.price);
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 pt-24 pb-16 px-4">
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-4">
            <Sparkles size={14} className="text-primary-400" />
            <span className="text-sm text-primary-300">Simple Pricing</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Choose Your Plan</h1>
          <p className="text-gray-400 text-lg">One-time payment. No subscriptions. No hidden fees.</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {/* Account Package */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="bg-gray-900/50 border-2 border-primary-500/30 rounded-3xl p-8 text-center relative overflow-hidden h-full flex flex-col">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 to-accent-500" />
              <div className="w-16 h-16 bg-primary-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Sparkles size={32} className="text-primary-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-300 mb-2">Create Foreign TikTok</h2>
              <div className="mb-6">
                <span className="text-5xl font-bold text-white">₦{price.toLocaleString()}</span>
                <span className="text-gray-500 ml-2">one-time</span>
              </div>

              <ul className="space-y-3 text-left mb-8 flex-1">
                {[
                  'Foreign TikTok account (UK, USA, Canada, etc.)',
                  'Custom username of your choice',
                  'Step-by-step setup guidance',
                  'Tips to avoid getting banned',
                  'Email support for 7 days',
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={12} className="text-green-400" />
                    </div>
                    <span className="text-sm text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/create-request"
                className="w-full inline-flex items-center justify-center gap-2 py-4 px-6 bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-primary-500/25"
              >
                Create Now <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>

          {/* Learning Package */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="bg-gray-900/50 border-2 border-accent-500/30 rounded-3xl p-8 text-center relative overflow-hidden h-full flex flex-col">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent-500 to-yellow-500" />
              <div className="w-16 h-16 bg-accent-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <GraduationCap size={32} className="text-accent-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-300 mb-2">Learn How To Create</h2>
              <div className="mb-6">
                <span className="text-5xl font-bold text-white">₦10,000</span>
                <span className="text-gray-500 ml-2">one-time</span>
              </div>

              <ul className="space-y-3 text-left mb-8 flex-1">
                {[
                  'Complete TikTok growth course',
                  'Monetization strategies guide',
                  'Content creation masterclass',
                  'Algorithm hacks & viral tips',
                  'Lifetime access to updates',
                  'Priority support for 30 days',
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={12} className="text-green-400" />
                    </div>
                    <span className="text-sm text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/learn"
                className="w-full inline-flex items-center justify-center gap-2 py-4 px-6 bg-gradient-to-r from-accent-500 to-yellow-500 hover:from-accent-600 hover:to-yellow-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-accent-500/25"
              >
                Learn Now <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="grid md:grid-cols-3 gap-6 mt-12"
        >
          {[
            { icon: Shield, title: 'Secure Payment', desc: 'Pay safely via Flutterwave. Your payment is encrypted and secure.' },
            { icon: Zap, title: 'Fast Delivery', desc: 'Your account is created and delivered within 24 hours.' },
            { icon: MessageCircle, title: 'Live Support', desc: 'Get help via WhatsApp or email whenever you need it.' },
          ].map((item, i) => (
            <div key={i} className="bg-gray-900/50 border border-gray-800/60 rounded-2xl p-6 text-center">
              <div className="w-12 h-12 bg-primary-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <item.icon size={22} className="text-primary-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-gray-400">{item.desc}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing;